import React, { useState } from 'react'
import { Box, Checkbox, FormControlLabel, IconButton, Menu, Tooltip } from '@mui/material'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import StatTile from '../components/StatTile'
import { usePanelConfig } from '../dashboard/panelConfigContext'
import { useAttention, usePatientSummary, useReflections, quietFromPatient } from '../api/selectors'
import { formatDay, relativeDay } from '../format'
import { advancedColors } from '../theme'
import { useTrackCatalog } from '../api/useTrackCatalog'
import { trackTitle } from '../dashboard/tracks/trackTransitions'

type MetricId = 'activeTracks' | 'lessons' | 'reflections' | 'lastActivity' | 'stalled' | 'started'

const METRIC_IDS: MetricId[] = ['activeTracks', 'lessons', 'reflections', 'lastActivity', 'stalled', 'started']

const METRIC_LABELS: Record<MetricId, string> = {
  activeTracks: 'Active tracks',
  lessons: 'Lessons complete',
  reflections: 'Reflections',
  lastActivity: 'Last activity',
  stalled: 'Stalled tracks',
  started: 'First enrolled',
}

const isMetricId = (value: unknown): value is MetricId => METRIC_IDS.includes(value as MetricId)

const metricsFromConfig = (config: unknown): MetricId[] => {
  const metrics = typeof config === 'object' && config !== null ? (config as { metrics?: unknown }).metrics : undefined

  if (!Array.isArray(metrics)) {
    return METRIC_IDS
  }

  const known = metrics.filter(isMetricId)

  return known.length > 0 ? known : METRIC_IDS
}

// Every hook runs unconditionally so the metric picker never changes the hook count.
const useMetricValues = (): Record<MetricId, { value: React.ReactNode; caption?: string }> => {
  const summary = usePatientSummary()
  const attention = useAttention()
  const reflectionCount = useReflections().length
  const lessonCount = summary.tracks.reduce((total, track) => total + track.completedCount, 0)
  const activity = quietFromPatient(summary)
  const catalog = useTrackCatalog()

  return {
    activeTracks: { value: summary.activeTrackCount, caption: `${summary.tracks.length} in total` },
    lessons: { value: lessonCount },
    reflections: { value: reflectionCount },
    lastActivity: { value: activity ? relativeDay(activity) : '—' },
    stalled: {
      value: attention.stalledTracks.length,
      caption: attention.stalledTracks.map((trackKey) => trackTitle(catalog, trackKey)).join(', ') || undefined,
    },
    started: { value: summary.startedAt ? formatDay(summary.startedAt) : '—' },
  }
}

const MetricsOverviewPanel = () => {
  const { config, setConfig } = usePanelConfig()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const values = useMetricValues()
  const shown = metricsFromConfig(config)

  const toggle = (id: MetricId) => {
    if (!setConfig) {
      return
    }

    const next = shown.includes(id) ? shown.filter((metric) => metric !== id) : [...shown, id]

    setConfig({ metrics: next.length > 0 ? next : shown })
  }

  return (
    <Box>
      {setConfig && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Choose metrics">
            <IconButton
              size="small"
              aria-label="Choose metrics"
              aria-haspopup="true"
              onClick={(event) => setAnchor(event.currentTarget)}
              sx={{ color: advancedColors.inkMuted }}
            >
              <SettingsRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
        {METRIC_IDS.filter((id) => shown.includes(id)).map((id) => (
          <StatTile key={id} label={METRIC_LABELS[id]} value={values[id].value} caption={values[id].caption} />
        ))}
      </Box>

      <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
        {METRIC_IDS.map((id) => (
          <Box key={id} sx={{ px: 2 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={shown.includes(id)} onChange={() => toggle(id)} />}
              label={METRIC_LABELS[id]}
            />
          </Box>
        ))}
      </Menu>
    </Box>
  )
}

export default MetricsOverviewPanel
